import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useContextMenu from '../../../../../components/v2/ContextMenu/useContextMenu';
import TreeItem from '../../../../../components/v2/TreeView/TreeItem';
import TreeView from '../../../../../components/v2/TreeView/TreeView';
import repeat from '../../../../../utils/repeat';
import { Dir } from '../../../../dirs';
import { useDirectoryActions } from '../../../../dirs/components/DirectoryActions';
import {
  useDirQuery,
  useDirsActions,
  useLoadedDirs,
  useSingleDir,
} from '../../../../dirs/hooks';
import { useDrawerSelection } from '../../providers/DrawerSelectionProvider';

type TreeNode = {
  id: string;
  label: string;
  children: TreeNode[];
};

const MAX_PLACEHOLDERS = 3;

// Placeholder ids must be negative (TreeView treats them as skeletons) and
// stable across renders, otherwise every re-render remounts the skeletons.
const createPlaceholderNodes = (parentId: number, count: number) =>
  repeat(count, (index) => ({
    id: String(-(parentId * 10 + index + 1)),
    label: '',
    isPlaceholder: true,
    children: [],
  }));

const DirectoryNavItem = (props: any) => {
  const { itemId } = props;
  const { dir } = useSingleDir(+itemId);
  const actions = useDirectoryActions({
    id: +itemId,
    metadata: dir?.metadata,
  });
  const dirQuery = useDirQuery(itemId, {
    refetchOnMount: false,
    enabled: false,
  });

  const { ref } = useContextMenu<HTMLDivElement>({
    actions,
    onOpen() {
      void dirQuery.refetch();
    },
  });

  return <TreeItem {...props} ref={ref} />;
};

const NavItem = (props: any) =>
  Number(props.itemId) > 0 ? (
    <DirectoryNavItem {...props} />
  ) : (
    <TreeItem {...props} />
  );

const TreeNavigation = () => {
  const { dirs, isLoading } = useLoadedDirs();
  const { fetchDirs } = useDirsActions();
  const navigate = useNavigate();
  const drawerSelection = useDrawerSelection();
  const child: { [key: string]: Dir[] } = {};

  const onItemClick = (event: React.MouseEvent, itemId: string) => {
    event.preventDefault();
    event.stopPropagation();

    // ignore placeholder IDs which are negative
    if (Number(itemId) > 0) {
      navigate(`/v2/directory/${itemId}`);
      fetchDirs({ parentId: +itemId });
      drawerSelection.setItem({ type: 'directory', id: +itemId });
    }

    return false;
  };

  useEffect(() => {
    fetchDirs();
  }, []);

  const mapToTreeNode = (item: Dir): TreeNode => {
    const itemId = item.id.toString();
    const loadedChildren = (child[itemId] || []).map(mapToTreeNode);
    const placeholderChildren = createPlaceholderNodes(
      item.id,
      Math.min(item.childrenCount, MAX_PLACEHOLDERS),
    );

    return {
      id: itemId,
      label: item.title || '',
      children:
        loadedChildren.length === 0 ? placeholderChildren : loadedChildren,
    };
  };

  const data = dirs
    .filter((item) => {
      const parentId = String(item.parentId || '');

      if (parentId) {
        child[parentId] = child[parentId] || [];
        child[parentId].push(item);
      }

      return parentId === '';
    })
    .map(mapToTreeNode);

  if (isLoading) {
    return <TreeView items={createPlaceholderNodes(0, 7)} />;
  }

  return (
    <TreeView
      items={data}
      onItemClick={onItemClick}
      slots={{
        item: NavItem,
      }}
      onItemExpand={(event, itemId) => {
        fetchDirs({ parentId: +itemId });
      }}
    />
  );
};

export default TreeNavigation;
